import { observer, useLocalObservable } from 'mobx-react';
import {
  FUND_FIELDS,
  PORTFOLIO_FIELDS,
  INVEST_PROFILE_FIELDS
} from '../../../utils/constants/fields';
import { useStore } from '../../../store/store';
import { formatNumberRange } from '../../../utils/utils';
import IconButton, {
  ICON_BUTTON_TYPES,
  ICON_BUTTON_ICONS
} from '../../../components/buttons/IconButton';
import { SHARED_PROJECT_TYPE_TABS } from './constants';
import FundDetailsPopup from '../../../components/matchmaker/FundDetailsPopup';
import PortfolioCoDetailsPopup from '../../../components/matchmaker/PortfolioCoDetailsPopup';
import InvestProfileDetailsPopup from '../../../components/matchmaker/InvestProfileDetailsPopup';
import { useCallback, useEffect } from 'react';
import ChipsList from '../../../components/common/ChipsList';

const fieldsByType = {
  funds: FUND_FIELDS,
  portfolios: PORTFOLIO_FIELDS,
  investprofiles: INVEST_PROFILE_FIELDS
};

const FeedbackSearchResultsTabEntry = observer(
  ({
    entry = {},
    feedbacks = {},
    project = {},
    type = 'funds',
    onChangeFeedback,
    params,
    index
  }) => {
    const { utilsStore } = useStore();

    const state = useLocalObservable(() => ({
      isDetailsPopupOpen: false,
      toggleDetailsPopup: () => {
        state.isDetailsPopupOpen = !state.isDetailsPopupOpen;
        utilsStore.lockScroll(state.isDetailsPopupOpen);
      },
      entryFeedback: [],
      get layout() {
        return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
      }
    }));

    useEffect(() => {
      return () => {
        if (state.isDetailsPopupOpen) {
          state.toggleDetailsPopup();
        }
      };
    }, [state, entry.id]);

    useEffect(() => {
      state.feedbacks = project.feedbacks.find((project) => project.resultId === entry.id);
    }, [entry, project]);
    const closeThePopUp = () => {
      if (state.isDetailsPopupOpen) {
        state.toggleDetailsPopup();
      }
    };

    const isFund = type === 'funds';
    const isPortfolio = type === 'portfolios';
    const isProfile = type === 'investprofiles';

    const fields = fieldsByType[type];

    const isFeedbackMarked = useCallback(
      (resultId, feedback) => {
        return feedbacks.find((fb) => fb.resultId === resultId && fb.feedback === feedback);
      },
      [feedbacks]
    );

    const readOnly = params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.RESOLVED;

    return (
      <div
        className="row result-row-entry funds-search-cols"
        style={index === 0 ? { marginTop: '8px' } : {}}>
        {state.isDetailsPopupOpen &&
          (isFund ? (
            <FundDetailsPopup fund={entry} onClose={closeThePopUp} />
          ) : isPortfolio ? (
            <PortfolioCoDetailsPopup portfolio={entry} onClose={closeThePopUp} />
          ) : (
            <InvestProfileDetailsPopup profile={entry} onClose={closeThePopUp} />
          ))}
        {state.layout === 'mobile' && (
          <div className="item feedback">
            <IconButton
              type={ICON_BUTTON_TYPES.GREEN}
              icon={ICON_BUTTON_ICONS.HAND_THUMBS_UP}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'up')}
              filled={isFeedbackMarked(entry.id, 'up')}
            />
            <IconButton
              type={ICON_BUTTON_TYPES.YELLOW}
              icon={ICON_BUTTON_ICONS.QUESTION_LG}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'question')}
              filled={isFeedbackMarked(entry.id, 'question')}
            />
            <IconButton
              type={ICON_BUTTON_TYPES.RED}
              icon={ICON_BUTTON_ICONS.HAND_THUMBS_DOWN}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'down')}
              filled={isFeedbackMarked(entry.id, 'down')}
            />
          </div>
        )}
        <div className="item name">
          <div className="label">
            {isFund ? 'Fund name' : isPortfolio ? 'Portfolio name' : 'Profile name'}
          </div>
          <div className="value">{entry[fields.NAME.NAME] || entry.companyName}</div>
        </div>
        <div className="item industry-focus">
          <div className="label">Industry focus</div>
          <div className="value">
            {/* {utilsStore
              .shortenList(utilsStore.getOptionName(entry, fields.INDUSTRIES), 2)
              .join(', ')} */}
            <ChipsList
              list={utilsStore.getOptionName(entry, fields.INDUSTRIES)}
              alignTopMeasurementsEnabled
              alignTopMeasurementsRowQuerySelector=".page-team-feedback > .feedback-details > .lists > .lists > .table-responsive > .row"
              alignTopMeasurementsItemsContainersQuerySelector="> .item"
            />
          </div>
        </div>
        <div className="item geo-focus">
          <div className="label">Geo focus</div>
          <div className="value">
            {/* {utilsStore
              .shortenList(utilsStore.getOptionName(entry, fields.GEOGRAPHIES), 3)
              .join(', ')} */}
            <ChipsList list={utilsStore.getOptionName(entry, fields.GEOGRAPHIES)} />
          </div>
        </div>
        <div className="item enterprise-value">
          <div className="label">Enterprise value (€M)</div>
          <div className="value">
            {formatNumberRange(
              entry[fields.ENTERPRISE_RANGE_MIN.NAME],
              entry[fields.ENTERPRISE_RANGE_MAX.NAME]
            )}
          </div>
        </div>
        <div className="item equity-value">
          <div className="label">Equity value (€M)</div>
          <div className="value">
            {formatNumberRange(
              entry[fields.EQUITY_RANGE_MIN.NAME],
              entry[fields.EQUITY_RANGE_MAX.NAME]
            )}
          </div>
        </div>

        {state.layout === 'desktop' && (
          <div className="item feedback">
            <IconButton
              type={ICON_BUTTON_TYPES.GREEN}
              icon={ICON_BUTTON_ICONS.HAND_THUMBS_UP}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'up')}
              filled={isFeedbackMarked(entry.id, 'up')}
              wrapClassName={readOnly ? 'force-default-cursor' : ''}
            />
            <IconButton
              type={ICON_BUTTON_TYPES.YELLOW}
              icon={ICON_BUTTON_ICONS.QUESTION_LG}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'question')}
              filled={isFeedbackMarked(entry.id, 'question')}
              wrapClassName={readOnly ? 'force-default-cursor' : ''}
            />
            <IconButton
              type={ICON_BUTTON_TYPES.RED}
              icon={ICON_BUTTON_ICONS.HAND_THUMBS_DOWN}
              onClick={() => !readOnly && onChangeFeedback(entry.id, 'down')}
              filled={isFeedbackMarked(entry.id, 'down')}
              wrapClassName={readOnly ? 'force-default-cursor' : ''}
            />
          </div>
        )}
        <div className="item actions funds-search-actions">
          {state.layout === 'desktop' && (
            <IconButton
              wrapClassName="view-details-btn-desktop"
              tooltipText="View details"
              onClick={state.toggleDetailsPopup}
              type={ICON_BUTTON_TYPES.BLUE}
              icon={ICON_BUTTON_ICONS.VIEW_DETAILS_CUSTOM1}
            />
          )}
          {state.layout === 'mobile' && (
            <IconButton
              wrapClassName="view-details-btn-mobile"
              innerText="View details"
              onClick={state.toggleDetailsPopup}
              type={ICON_BUTTON_TYPES.BLUE}
              filled
            />
          )}
        </div>
      </div>
    );
  }
);

export default FeedbackSearchResultsTabEntry;
